/**
 * @module components/PivotTable
 * @description 交叉表配置（对齐 ChartsDrop PivotTable）
 *
 * 状态管理模式：
 *   读: useSettingObjectState('crosstab') + hasStoredCrosstab（无落库时 UI 仍由 QB 推导）
 *   写: setValue(crosstab) — 用户拖拽后落 draft
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'umi';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import TwoDragList from '@/pages/datalab/components/TwoDragList';
import { DATALAB_COMPARE_USER_FILTERS } from '@/pages/datalab/components/UserFilter/FilterPanel/constant';
import { COLUMN_DISPLAY_TEXT } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/constants';
import { ChartType } from '@dbl/core/chart';
import { LANGUAGE_EN } from '@home/_config/config';
import type { ICrosstabConfig, ICrosstabFieldRef, IPivotTableDragItem } from './types';
import {
  useChartSettingsSelector,
  useSettingObjectState,
} from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import { toChartTypeEnum } from '../../utils/toChartTypeEnum';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import './index.scss';

const getFieldKey = (tableCode: string, column: string): string =>
  `${tableCode}_${column}`;

interface IAnalysisField {
  table_code?: string;
  column_name?: string;
  column_display?: string;
  column_display_en?: string;
}

interface IUserFilterField {
  is_all_users?: boolean;
}

const normalizeTableCode = (tableCode: string | undefined): string => {
  const normalized = (tableCode ?? '').trim();
  return normalized || 't0';
};

const buildPivotCompareDimension = (): IAnalysisField => ({
  table_code: '',
  column_name: DATALAB_COMPARE_USER_FILTERS,
  column_display: COLUMN_DISPLAY_TEXT.COMPARISON_GROUP.CN,
  column_display_en: COLUMN_DISPLAY_TEXT.COMPARISON_GROUP.EN,
});

const normalizeDimensionFields = (
  fields: IAnalysisField[],
  shouldInjectCompareDimension: boolean,
): IAnalysisField[] => {
  const sourceFields = shouldInjectCompareDimension
    ? [buildPivotCompareDimension(), ...fields]
    : fields;

  return sourceFields.map(field => {
    const isCompareDimension =
      field.column_name === DATALAB_COMPARE_USER_FILTERS;
    return {
      ...field,
      table_code: isCompareDimension ? '' : normalizeTableCode(field.table_code),
    };
  });
};

const shouldInjectPivotCompareDimension = (params: {
  hasUserFilters: boolean;
  columnUserFilters: IUserFilterField[];
}): boolean => {
  const { hasUserFilters, columnUserFilters } = params;
  const onlyOneIsAllUsers =
    columnUserFilters.length === 1 &&
    columnUserFilters[0]?.is_all_users === true;

  return (
    hasUserFilters &&
    columnUserFilters.length > 0 &&
    !onlyOneIsAllUsers
  );
};

const PivotTable: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const { value: schemaCrosstab, setValue: setCrosstab } =
    useSettingObjectState('crosstab');
  const hasStoredCrosstab = useChartSettingsSelector(
    s => 'crosstab' in s.draft || 'crosstab' in s.base,
  );

  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const columnDimensions = useQuerySelector(
    s => s.columnDimensions ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const columnUserFilters = useQuerySelector(
    s => s.columnUserFilters ?? EMPTY_COLUMN_INFO_LIST,
  );
  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );
  const hasUserFilters = columnUserFilters.length > 0;

  const persistedCrosstab = hasStoredCrosstab ? schemaCrosstab : undefined;

  const hasValidPersistedCrosstab = Boolean(
    persistedCrosstab &&
      Array.isArray(persistedCrosstab.rows) &&
      Array.isArray(persistedCrosstab.columns),
  );

  /** 无 base/draft 时仅用于 UI 派生，不写入 Context（避免「仅浏览高级 Tab」触发上传） */
  const crosstabForDisplay = hasValidPersistedCrosstab
    ? persistedCrosstab
    : undefined;

  const injectPivotCompareGroupDimension = useMemo(
    () =>
      shouldInjectPivotCompareDimension({
        hasUserFilters: !!hasUserFilters,
        columnUserFilters: (columnUserFilters || []) as IUserFilterField[],
      }),
    [columnUserFilters, hasUserFilters],
  );

  const normalizedColumnDimensions = useMemo(
    () =>
      normalizeDimensionFields(
        (columnDimensions || []) as IAnalysisField[],
        injectPivotCompareGroupDimension,
      ),
    [columnDimensions, injectPivotCompareGroupDimension],
  );

  const normalizedColumnMetrics = useMemo(
    () =>
      ((columnMetrics || []) as IAnalysisField[]).map(field => ({
        ...field,
        table_code: normalizeTableCode(field.table_code),
      })),
    [columnMetrics],
  );

  const dimensionIdSet = useMemo(() => {
    const set = new Set<string>();
    normalizedColumnDimensions.forEach(dim => {
      const tableCode = dim.table_code || '';
      const columnName = dim.column_name;
      if (!columnName) return;
      set.add(getFieldKey(tableCode, columnName));
    });
    return set;
  }, [normalizedColumnDimensions]);

  const metricMeta = useMemo(() => {
    const metricIdSet = new Set<string>();
    const metricRefs: ICrosstabFieldRef[] = [];

    normalizedColumnMetrics.forEach(metric => {
      const tableCode = metric.table_code || '';
      const columnName = metric.column_name;
      if (!columnName) return;
      metricIdSet.add(getFieldKey(tableCode, columnName));
      metricRefs.push({
        table_code: tableCode,
        column: columnName,
      });
    });

    return { metricIdSet, metricRefs };
  }, [normalizedColumnMetrics]);

  const { metricIdSet, metricRefs } = metricMeta;

  const selectedDimensionRefsOrdered = useMemo<ICrosstabFieldRef[]>(() => {
    const columns = crosstabForDisplay?.columns;
    if (!Array.isArray(columns) || columns.length === 0) {
      return [];
    }

    const selected: ICrosstabFieldRef[] = [];
    const seen = new Set<string>();
    columns.forEach((field: ICrosstabFieldRef) => {
      const tableCode = field.table_code || '';
      const columnName = field?.column;
      if (!columnName) return;
      const key = getFieldKey(tableCode, columnName);
      if (!dimensionIdSet.has(key)) return;
      if (metricIdSet.has(key)) return;
      if (seen.has(key)) return;
      seen.add(key);
      selected.push({ table_code: tableCode, column: columnName });
    });

    return selected;
  }, [crosstabForDisplay?.columns, dimensionIdSet, metricIdSet]);

  const metricsFirstInDraft = useMemo(() => {
    const columns = crosstabForDisplay?.columns;
    if (!Array.isArray(columns) || columns.length === 0) return true;

    for (const field of columns as ICrosstabFieldRef[]) {
      const tableCode = field.table_code || '';
      const columnName = field?.column;
      if (!columnName) continue;

      const key = getFieldKey(tableCode, columnName);
      if (metricIdSet.has(key)) return true;
      if (dimensionIdSet.has(key)) return false;
    }

    return true;
  }, [crosstabForDisplay?.columns, dimensionIdSet, metricIdSet]);

  const rowsRefs = useMemo<ICrosstabFieldRef[]>(() => {
    const selectedDimensionIdSet = new Set<string>();
    selectedDimensionRefsOrdered.forEach(ref => {
      selectedDimensionIdSet.add(getFieldKey(ref.table_code, ref.column));
    });

    return normalizedColumnDimensions
      .map(dim => ({
        table_code: dim.table_code || '',
        column: dim.column_name || '',
      }))
      .filter(ref => Boolean(ref.column))
      .filter(
        ref =>
          !selectedDimensionIdSet.has(
            getFieldKey(ref.table_code, ref.column),
          ),
      );
  }, [normalizedColumnDimensions, selectedDimensionRefsOrdered]);

  const topItems = useMemo<IPivotTableDragItem[]>(() => {
    return rowsRefs.map(ref => {
      const column = normalizedColumnDimensions.find(
        dim =>
          (dim.table_code || '') === ref.table_code &&
          dim.column_name === ref.column,
      );
      const text = isEn
        ? column?.column_display_en || ref.column
        : column?.column_display || ref.column;

      return {
        id: getFieldKey(ref.table_code, ref.column),
        text,
        kind: 'dimension' as const,
        className: 'dbl-pivot-table-setting__item--dimension',
        tableCode: ref.table_code,
        columnName: ref.column,
      };
    });
  }, [isEn, normalizedColumnDimensions, rowsRefs]);

  const bottomItems = useMemo<IPivotTableDragItem[]>(() => {
    const metricItems: IPivotTableDragItem[] = metricRefs.map(ref => {
      const metric = normalizedColumnMetrics.find(
        m =>
          (m.table_code || '') === ref.table_code &&
          m.column_name === ref.column,
      );
      const text = isEn
        ? metric?.column_display_en || ref.column
        : metric?.column_display || ref.column;

      return {
        id: getFieldKey(ref.table_code, ref.column),
        text,
        kind: 'metric' as const,
        className: 'dbl-pivot-table-setting__item--metric',
        tableCode: ref.table_code,
        columnName: ref.column,
        dragDisabled: true,
      };
    });

    const dimensionItems: IPivotTableDragItem[] =
      selectedDimensionRefsOrdered.map(ref => {
        const dim = normalizedColumnDimensions.find(
          d =>
            (d.table_code || '') === ref.table_code &&
            d.column_name === ref.column,
        );
        const text = isEn
          ? dim?.column_display_en || ref.column
          : dim?.column_display || ref.column;

        return {
          id: getFieldKey(ref.table_code, ref.column),
          text,
          kind: 'dimension' as const,
          className: 'dbl-pivot-table-setting__item--dimension',
          tableCode: ref.table_code,
          columnName: ref.column,
        };
      });

    return metricsFirstInDraft
      ? [...metricItems, ...dimensionItems]
      : [...dimensionItems, ...metricItems];
  }, [
    isEn,
    metricRefs,
    metricsFirstInDraft,
    normalizedColumnDimensions,
    normalizedColumnMetrics,
    selectedDimensionRefsOrdered,
  ]);

  const lastDraggedDimensionIdRef = useRef<string | null>(null);
  const currentBottomIdsRef = useRef<string[]>(
    bottomItems.map(it => String(it.id)),
  );

  const handleMove = useCallback(
    (evt: {
      dragged?: { dataset?: { id?: string } };
      to?: HTMLElement;
      related?: { dataset?: { id?: string } };
      willInsertAfter?: boolean;
    }) => {
      const draggedId = evt?.dragged?.dataset?.id as string | undefined;
      if (!draggedId) return true;

      if (metricIdSet.has(draggedId)) return false;

      const toSide = evt?.to
        ?.closest?.('[data-two-drag-side]')
        ?.getAttribute?.('data-two-drag-side');
      if (toSide === 'bottom') {
        const metricIndexes = currentBottomIdsRef.current
          .map((id, index) => (metricIdSet.has(id) ? index : -1))
          .filter(index => index !== -1);

        if (metricIndexes.length > 0) {
          const firstMetricIndex = metricIndexes[0];
          const lastMetricIndex = metricIndexes[metricIndexes.length - 1];
          const relatedId = evt?.related?.dataset?.id;
          const relatedIndex = relatedId
            ? currentBottomIdsRef.current.indexOf(String(relatedId))
            : -1;

          if (relatedIndex !== -1) {
            const insertIndex = evt?.willInsertAfter
              ? relatedIndex + 1
              : relatedIndex;
            const insertInsideMetricBlock =
              insertIndex > firstMetricIndex &&
              insertIndex < lastMetricIndex + 1;

            if (insertInsideMetricBlock) {
              return false;
            }
          }
        }
      }

      lastDraggedDimensionIdRef.current = draggedId;
      return true;
    },
    [metricIdSet],
  );

  useEffect(() => {
    currentBottomIdsRef.current = bottomItems.map(it => String(it.id));
  }, [bottomItems]);

  const handleChange = useCallback(
    ({
      topItems: nextTopItems,
      bottomItems: nextBottomItems,
    }: {
      topItems: IPivotTableDragItem[];
      bottomItems: IPivotTableDragItem[];
    }) => {
      const nextRows: ICrosstabFieldRef[] = nextTopItems.map(item => ({
        table_code: item.tableCode,
        column: item.columnName,
      }));

      const nextDimensionColumns: ICrosstabFieldRef[] = nextBottomItems
        .filter(item => item.kind === 'dimension')
        .map(item => ({
          table_code: item.tableCode,
          column: item.columnName,
        }));

      const nextMetricIdSet = new Set(
        nextBottomItems
          .filter(item => item.kind === 'metric')
          .map(item => String(item.id)),
      );

      const nextMetricsFromBottom: ICrosstabFieldRef[] = nextBottomItems
        .filter(item => item.kind === 'metric')
        .map(item => ({
          table_code: item.tableCode,
          column: item.columnName,
        }));

      const metricsOrderedFromState: ICrosstabFieldRef[] = metricRefs.filter(
        ref => nextMetricIdSet.has(getFieldKey(ref.table_code, ref.column)),
      );

      const nextMetrics: ICrosstabFieldRef[] = metricsOrderedFromState.length
        ? metricsOrderedFromState
        : nextMetricsFromBottom;

      const draggedId = lastDraggedDimensionIdRef.current;
      const firstMetricIndexInNext = nextBottomItems.findIndex(
        item => item.kind === 'metric',
      );
      const draggedIndexInNext = draggedId
        ? nextBottomItems.findIndex(item => String(item.id) === draggedId)
        : -1;

      const preFirstId = currentBottomIdsRef.current[0];
      const preMetricsFirst = preFirstId ? metricIdSet.has(preFirstId) : false;

      let metricsFirstChosen: boolean;
      if (draggedIndexInNext !== -1 && firstMetricIndexInNext !== -1) {
        metricsFirstChosen = draggedIndexInNext > firstMetricIndexInNext;
      } else {
        metricsFirstChosen = nextBottomItems[0]?.kind === 'metric';
      }

      let nextDimensionColumnsFinal = nextDimensionColumns;
      if (metricsFirstChosen !== preMetricsFirst) {
        const preDimensionOrderIds = currentBottomIdsRef.current.filter(
          id => !metricIdSet.has(id),
        );

        const nextDimensionIdToRef = new Map<string, ICrosstabFieldRef>();
        nextBottomItems
          .filter(item => item.kind === 'dimension')
          .forEach(item => {
            nextDimensionIdToRef.set(String(item.id), {
              table_code: item.tableCode,
              column: item.columnName,
            });
          });

        const orderedDimensionColumns: ICrosstabFieldRef[] = [];
        preDimensionOrderIds.forEach(id => {
          const field = nextDimensionIdToRef.get(id);
          if (field) {
            orderedDimensionColumns.push(field);
          }
        });

        if (orderedDimensionColumns.length === nextDimensionColumns.length) {
          nextDimensionColumnsFinal = orderedDimensionColumns;
        }
      }

      const nextColumns: ICrosstabFieldRef[] = metricsFirstChosen
        ? [...nextMetrics, ...nextDimensionColumnsFinal]
        : [...nextDimensionColumnsFinal, ...nextMetrics];

      lastDraggedDimensionIdRef.current = null;

      setCrosstab({
        rows: nextRows,
        columns: nextColumns,
      });
    },
    [metricIdSet, metricRefs, setCrosstab],
  );

  if (chartType !== ChartType.Table) {
    return null;
  }

  return (
    <ConfigCollapseCard
      title={formatMessage({ id: 'dbl_chart_pivot_crosstab_setting' })}
      className="dbl-pivot-table-crosstab-setting"
      defaultActiveIds={[]}
    >
      <div className="dbl-pivot-table-setting">
        <div className="dbl-pivot-table-setting__list">
          <TwoDragList
            topItems={topItems}
            bottomItems={bottomItems}
            onChange={handleChange}
            topTitle={formatMessage({ id: 'dbl_chart_pivot_row' })}
            bottomTitle={formatMessage({ id: 'dbl_chart_pivot_column' })}
            maxHeight={280}
            topSortable={false}
            bottomSortable
            onMove={handleMove}
          />
        </div>
      </div>
    </ConfigCollapseCard>
  );
};

export default React.memo(PivotTable);

